import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';

const SolutionsPage: React.FC = () => {
  const navigate = useNavigate();

  const solutions = [
    { title: 'Research Organizations', subtitle: 'Academic & Non-Profit', icon: 'pi pi-graduation-cap', color: '#3B82F6', benefits: ['Reduce timeline by 70%', 'Ensure consistency', 'Automate compliance', 'Scale capacity'], useCases: ['Impact evaluations', 'Cohort studies', 'Policy research', 'Program evaluations'] },
    { title: 'Government Agencies', subtitle: 'Policy & Program Evaluation', icon: 'pi pi-building', color: '#22C55E', benefits: ['Accelerate policy development', 'Ensure audit compliance', 'Reduce costs', 'Improve decisions'], useCases: ['Impact assessments', 'Citizen surveys', 'Policy studies', 'Budget research'] },
    { title: 'Consulting Firms', subtitle: 'Client Research Services', icon: 'pi pi-briefcase', color: '#A855F7', benefits: ['Increase margins', 'Reduce timelines', 'Scale offerings', 'Ensure quality'], useCases: ['Market research', 'Organizational assessments', 'Strategy research', 'Due diligence'] },
    { title: 'International Organizations', subtitle: 'Global Development', icon: 'pi pi-globe', color: '#F97316', benefits: ['Standardize operations', 'Reduce overhead', 'Ensure comparability', 'Accelerate delivery'], useCases: ['Cross-country evaluations', 'Global health surveys', 'Education assessments', 'Economic studies'] }
  ];

  const industries = [
    { name: 'Healthcare & Public Health', description: 'Patient outcomes, health programs, epidemiological research', icon: 'pi pi-heart', color: '#EF4444' },
    { name: 'Education', description: 'Learning outcomes, program evaluations, institutional research', icon: 'pi pi-book', color: '#3B82F6' },
    { name: 'Social Services', description: 'Program impact, beneficiary assessments, service delivery', icon: 'pi pi-users', color: '#22C55E' },
    { name: 'Economic Development', description: 'Market research, economic impact, business environment', icon: 'pi pi-chart-line', color: '#F97316' }
  ];

  const stats = [
    { value: '90%', label: 'Time Reduction', sub: 'Average across sectors' },
    { value: '75%', label: 'Cost Savings', sub: 'Operational reduction' },
    { value: '10x', label: 'Faster Deployment', sub: 'Survey to collection' },
    { value: '100%', label: 'Audit Compliance', sub: 'Complete trails' }
  ];

  const cardStyle: React.CSSProperties = { backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' };
  const iconStyle = (color: string): React.CSSProperties => ({ width: '3.5rem', height: '3.5rem', borderRadius: '12px', backgroundColor: color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 });

  return (
    <div>
      {/* Hero */}
      <div style={{ padding: '3rem 2rem', textAlign: 'center', backgroundColor: '#f8f9fa' }}>
        <h1 style={{ fontSize: '2.5rem', margin: '0 0 1rem 0', color: '#1f2937' }}>Solutions for Every Research Need</h1>
        <p style={{ color: '#6b7280', fontSize: '1.125rem', maxWidth: '700px', margin: '0 auto 2rem auto' }}>
          Whether you're a research organization, government agency, consulting firm, or international organization, our platform adapts to your specific requirements.
        </p>
        <Button label="Find Your Solution" icon="pi pi-search" className="p-button-lg" onClick={() => navigate('/contact')} />
      </div>

      {/* Solutions by Organization */}
      <div style={{ padding: '3rem 2rem', backgroundColor: 'white' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '2rem', color: '#1f2937', margin: '0 0 0.5rem 0' }}>Tailored for Your Organization</h2>
          <p style={{ color: '#6b7280', maxWidth: '600px', margin: '0 auto' }}>Specialized features and workflows for each sector</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1.5rem' }}>
          {solutions.map((sol, i) => (
            <div key={i} style={cardStyle}>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                <div style={iconStyle(sol.color)}><i className={sol.icon} style={{ color: 'white', fontSize: '1.5rem' }}></i></div>
                <div>
                  <h3 style={{ fontSize: '1.25rem', margin: '0 0 0.25rem 0', color: '#1f2937' }}>{sol.title}</h3>
                  <span style={{ color: '#3B82F6', fontSize: '0.875rem', fontWeight: 500 }}>{sol.subtitle}</span>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                <div>
                  <h4 style={{ fontSize: '0.875rem', color: '#1f2937', margin: '0 0 0.5rem 0' }}>Benefits</h4>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {sol.benefits.map((b, j) => (
                      <li key={j} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem', color: '#6b7280', fontSize: '0.875rem' }}>
                        <i className="pi pi-check" style={{ color: '#22C55E', marginRight: '0.5rem', fontSize: '0.75rem' }}></i>{b}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 style={{ fontSize: '0.875rem', color: '#1f2937', margin: '0 0 0.5rem 0' }}>Use Cases</h4>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {sol.useCases.map((u, j) => (
                      <li key={j} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem', color: '#6b7280', fontSize: '0.875rem' }}>
                        <i className="pi pi-arrow-right" style={{ color: '#3B82F6', marginRight: '0.5rem', fontSize: '0.75rem' }}></i>{u}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                <Button label="Learn More" icon="pi pi-arrow-right" className="p-button-outlined p-button-sm" style={{ width: '100%' }} onClick={() => navigate('/contact')} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Industry Applications */}
      <div style={{ padding: '3rem 2rem', backgroundColor: '#f8f9fa' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '2rem', color: '#1f2937', margin: '0 0 0.5rem 0' }}>Industry Applications</h2>
          <p style={{ color: '#6b7280' }}>Serving diverse industries with specialized research needs</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
          {industries.map((ind, i) => (
            <div key={i} style={{ ...cardStyle, textAlign: 'center' }}>
              <div style={{ width: '3rem', height: '3rem', borderRadius: '8px', backgroundColor: ind.color, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem auto' }}>
                <i className={ind.icon} style={{ color: 'white', fontSize: '1.25rem' }}></i>
              </div>
              <h3 style={{ fontSize: '1rem', margin: '0 0 0.5rem 0', color: '#1f2937' }}>{ind.name}</h3>
              <p style={{ color: '#6b7280', fontSize: '0.875rem', margin: 0 }}>{ind.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div style={{ padding: '3rem 2rem', backgroundColor: 'white' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '2rem', color: '#1f2937', margin: '0 0 0.5rem 0' }}>Proven Results Across Sectors</h2>
          <p style={{ color: '#6b7280' }}>Organizations consistently achieve significant improvements</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
          {stats.map((stat, i) => (
            <div key={i} style={{ textAlign: 'center', padding: '1.5rem' }}>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#3B82F6', marginBottom: '0.25rem' }}>{stat.value}</div>
              <div style={{ color: '#1f2937', fontWeight: 500, marginBottom: '0.25rem' }}>{stat.label}</div>
              <div style={{ color: '#9ca3af', fontSize: '0.875rem' }}>{stat.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div style={{ padding: '3rem 2rem', backgroundColor: '#3B82F6', textAlign: 'center' }}>
        <h2 style={{ color: 'white', fontSize: '2rem', margin: '0 0 1rem 0' }}>Ready to Transform Your Research Operations?</h2>
        <p style={{ color: '#bfdbfe', fontSize: '1.125rem', maxWidth: '600px', margin: '0 auto 2rem auto' }}>
          Let's discuss how our platform can be tailored to your specific needs.
        </p>
        <Button label="Schedule Consultation" icon="pi pi-calendar" className="p-button-lg" style={{ backgroundColor: 'white', color: '#3B82F6', border: 'none' }} onClick={() => navigate('/contact')} />
      </div>
    </div>
  );
};

export default SolutionsPage;
